import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, MapPin, ScanLine, Network, ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1583100913829-3e10c0110772?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxNZXhpY28lMjBjaXR5JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3Nzk0MTc5MHww&ixlib=rb-4.1.0&q=85";

const features = [
  {
    icon: Network,
    title: "Estructura territorial multi-rol",
    desc: "Configura subcuentas estatales o municipales y activa los perfiles que necesitas: candidato, coordinadores estatales, municipales, regionales y de colonia.",
  },
  {
    icon: ScanLine,
    title: "OCR de INE en tiempo real",
    desc: "El voluntario sube su INE y el sistema extrae nombre, CURP, dirección, colonia, estado y sección electoral con autocompletado contra el catálogo geoelectoral.",
  },
  {
    icon: MapPin,
    title: "Catálogo EDMSLM oficial",
    desc: "Cada subcuenta sincroniza solo el catálogo geoelectoral del INE para su estado o municipio. Cobertura precisa por sección, localidad y manzana.",
  },
  {
    icon: ShieldCheck,
    title: "Aprobaciones con bitácora",
    desc: "Los enlaces de reclutamiento quedan sujetos a aprobación con registro completo de quién aprobó, fecha y hora. Cumple con LFPDPPP.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-8 h-8 bg-slate-900 grid place-items-center rounded">
              <span className="text-white font-display font-black text-lg">W</span>
            </div>
            <div>
              <div className="font-display font-bold text-slate-900 text-lg leading-none">
                Noctua 360
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Plataforma multi-tenant
              </div>
            </div>
          </Link>
          <Link to="/login" data-testid="header-login-link">
            <Button className="btn-primary" data-testid="header-login-button">
              Iniciar sesión
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
        <div className="brand-stripe" />
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dotted-bg opacity-60" />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 animate-in">
            <span className="tracking-label text-blue-700">
              Sistema electoral · México
            </span>
            <h1 className="mt-4 font-display font-black text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Operación territorial seria,
              <br />
              <span className="text-blue-600">simple y auditable.</span>
            </h1>
            <p className="mt-6 text-base text-slate-600 max-w-xl leading-relaxed">
              Configura subcuentas independientes para cada candidatura, activa
              los roles que tu campaña necesita y monitorea cobertura en tiempo
              real con datos del catálogo geoelectoral oficial del INE.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login" data-testid="hero-cta-login">
                <Button className="btn-primary h-12 px-6" data-testid="hero-login-button">
                  Acceder al panel
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="#features" data-testid="hero-features-link">
                <Button variant="outline" className="h-12 px-6 border-slate-300" data-testid="hero-features-button">
                  Ver capacidades
                </Button>
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              <Stat n="32" l="Entidades" />
              <Stat n="2,477" l="Municipios" />
              <Stat n="68 K+" l="Secciones" />
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-4 line-pattern rounded-2xl" />
            <div className="relative rounded-2xl overflow-hidden border border-slate-200">
              <img src={HERO_IMG} alt="Arquitectura institucional" className="w-full h-[420px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-lg px-5 py-4 shadow-sm">
              <div className="tracking-label text-slate-500">Catálogo activo</div>
              <div className="font-display font-bold text-2xl text-slate-900">EDMSLM · INE</div>
              <div className="text-xs text-slate-500 mt-1">Edición vigente 2026-2027</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <span className="tracking-label text-blue-700">Capacidades</span>
            <h2 className="mt-3 font-display font-bold text-slate-900 text-2xl sm:text-3xl lg:text-4xl">
              Todo lo que tu estructura territorial necesita en un solo lugar.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((f, i) => (
              <div
                key={i}
                className="surface-card p-6 hover:-translate-y-1 hover:shadow-md transition-all duration-200"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-10 h-10 rounded bg-slate-900 grid place-items-center">
                  <f.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="mt-4 font-display font-bold text-xl text-slate-900">
                  {f.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-slate-900 grid place-items-center rounded">
              <span className="text-white font-display font-black text-xs">W</span>
            </div>
            <span className="font-display font-bold text-slate-900">Noctua 360</span>
          </div>
          <div>© 2026 — Plataforma cívica · LFPDPPP</div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display font-black text-2xl text-slate-900">{n}</div>
      <div className="tracking-label text-slate-500">{l}</div>
    </div>
  );
}
